import React from 'react';
import LoadingPage from './LoadingPage';
import MediaUploadField from '../../../puck/fields/MediaUploadField';
import { BlockImageField } from '../../../puck/fields/puckImageField';
import { puckColorField } from '../../../puck/fields/createPuckColorField';
import { puckPxSpacingField } from '../../../puck/fields/puckPxSpacingField';
import { puckNoteField } from '../../../puck/fields/puckNoteField';
import { puckSectionField } from '../../../puck/fields/puckSectionField';
import { canCountVideoLoops, MAX_VIDEO_LOOPS } from '../../../loading/loadingPageDuration';

const SPINNER_TYPE_OPTIONS = [
  { label: 'Cercle', value: 'oval' },
  { label: 'Barres', value: 'bars' },
  { label: 'Points', value: 'dots' },
];

function mediaUrlField(kind) {
  return {
    type: 'custom',
    label: kind === 'video' ? 'Vidéo' : 'Image',
    // L'image peut venir du CRM ; une vidéo, non.
    render: ({ name, onChange, value }) => (kind === 'image'
      ? <BlockImageField name={name} value={value} onChange={onChange} />
      : <MediaUploadField kind={kind} value={value} onChange={onChange} />),
  };
}

const loadingPageBaseFields = {
  _section1: puckSectionField('Média'),
  mediaKind: {
    type: 'select',
    label: 'Type de barre de chargement',
    /* Options trop longues pour la demi-largeur. */
    layout: 'stacked',
    options: [
      { label: 'Image', value: 'image' },
      { label: 'Vidéo', value: 'video' },
      { label: 'Spinner (chargement animé)', value: 'spinner' },
    ],
  },
  mediaUrlImage: mediaUrlField('image'),
  mediaUrlVideo: mediaUrlField('video'),
  mediaWidth: puckPxSpacingField('Largeur du média', 'Vide = prend le maximum de place disponible.'),
  spinnerType: {
    type: 'select',
    label: 'Style du spinner',
    options: SPINNER_TYPE_OPTIONS,
  },
  spinnerColor: puckColorField('Couleur du spinner', { allowTransparent: true }),
  spinnerSize: puckPxSpacingField('Taille du spinner'),

  _section2: puckSectionField('Style'),
  backgroundColor: puckColorField('Couleur de fond', { allowTransparent: true }),

  _section3: puckSectionField('Comportement'),
  durationSeconds: {
    type: 'number',
    label: 'Durée avant de passer au site (secondes)',
    min: 0.5,
    max: 120,
    step: 0.5,
  },
  endMode: {
    type: 'select',
    label: 'Fin de la page de chargement',
    /* Options trop longues pour la demi-largeur. */
    layout: 'stacked',
    options: [
      { label: 'Après une durée fixe', value: 'duration' },
      { label: 'À la fin de la vidéo', value: 'loops' },
    ],
  },
  videoLoops: {
    type: 'number',
    label: 'Nombre de lectures de la vidéo',
    min: 1,
    max: MAX_VIDEO_LOOPS,
    step: 1,
  },
  _noteEmbed: puckNoteField(
    'Un lien YouTube ou Vimeo ne peut pas être compté en lectures : la durée reste en secondes.',
  ),
};

function resolveLoadingPageFields(data, { fields }) {
  const mediaKind = data.props.mediaKind || 'spinner';

  const next = {
    _section1: fields._section1,
    mediaKind: fields.mediaKind,
  };

  // Section média/spinner juste après le sélecteur de type.
  if (mediaKind === 'image') {
    next.mediaUrlImage = fields.mediaUrlImage;
    next.mediaWidth = fields.mediaWidth;
  } else if (mediaKind === 'video') {
    next.mediaUrlVideo = fields.mediaUrlVideo;
    next.mediaWidth = fields.mediaWidth;
  } else {
    next.spinnerType = fields.spinnerType;
    next.spinnerColor = fields.spinnerColor;
    next.spinnerSize = fields.spinnerSize;
  }

  next._section2 = fields._section2;
  next.backgroundColor = fields.backgroundColor;
  next._section3 = fields._section3;

  /* Compter les lectures suppose un fichier que la balise `video` sait lire :
     sur un embed, on le dit et on garde le chronomètre. */
  if (mediaKind === 'video' && canCountVideoLoops(data.props)) {
    next.endMode = fields.endMode;
    if (data.props.endMode === 'loops') next.videoLoops = fields.videoLoops;
    else next.durationSeconds = fields.durationSeconds;
    return next;
  }

  if (mediaKind === 'video' && data.props.mediaUrlVideo) next._noteEmbed = fields._noteEmbed;
  next.durationSeconds = fields.durationSeconds;

  return next;
}

const loadingPageConfig = {
  label: 'Page de chargement',
  defaultProps: {
    backgroundColor: '#0f172a',
    mediaKind: 'spinner',
    mediaUrlImage: '',
    mediaUrlVideo: '',
    mediaWidth: '12.5rem', // = 200px
    spinnerType: 'oval',
    spinnerColor: '',
    spinnerSize: '3rem', // = 48px
    title: '',
    subtitle: '',
    durationSeconds: 3,
    endMode: 'duration',
    videoLoops: 1,
    fadeType: 'fade',
  },
  fields: loadingPageBaseFields,
  resolveFields: resolveLoadingPageFields,
  render: (props) => (
    <div style={{ position: 'relative', width: '100%', minHeight: 'calc(100vh - 56px)' }}>
      <LoadingPage {...props} puck={{ isEditing: true }} />
    </div>
  ),
};

export default loadingPageConfig;
